import { Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { BaseTableMapper } from "src/app/tinea-components/table/mapper/BaseTableMapper";
import { TableModel } from "src/app/tinea-components/table/models/input/table-model";
import { TableRows } from "src/app/tinea-components/table/models/input/table-rows";
import { TableTd } from "src/app/tinea-components/table/models/input/table-td";
import { TableTh } from "src/app/tinea-components/table/models/input/table-th";
import { ValueCell } from "src/app/tinea-components/table/models/input/value-cell";
import { AgentsEventsPaginatedList } from "src/swagger-gen/web-secured/models";

@Injectable({
  providedIn: "root",
})
export class TableAcquisitionLogListMapper extends BaseTableMapper {
  // -- configuration
  readonly CSS_CLASS_CONFIG = "table-acquistion-log";

  readonly DISPLAY_NO_DATA = "--";

  // -- table
  // dataId

  readonly COLUMN_ID_DATE = "DATE";
  readonly COLUMN_ID_TYPE = "TYPE";
  readonly COLUMN_ID_SUBTYPE = "SUBTYPE";
  readonly COLUMN_ID_CONNECTION_TYPE = "CONNECTION_TYPE";
  readonly COLUMN_ID_CONNECTION_NAME = "CONNECTION_NAME";
  readonly COLUMN_ID_CONNECTION_ID = "CONNECTION_ID";
  readonly COLUMN_ID_OPCUA_ID = "OPCUA_ID";
  readonly COLUMN_ID_OCPP_ID = "OCPP_ID";
  readonly COLUMN_ID_TAG_NAME = "TAG_NAME";
  readonly COLUMN_ID_DATA = "DATA";
  readonly COLUMN_ID_STATION = "STATION";

  table: TableModel;

  constructor(private translate: TranslateService) {
    super();
    this.table = new TableModel();
  }

  init(acquisitionLogs: AgentsEventsPaginatedList): TableModel {
    this.table = new TableModel();
    // configuration
    this.initConfiguration();
    // header
    this.initHeader();
    // data
    this.initDataRow(acquisitionLogs);

    return this.table;
  }

  initConfiguration(): void {
    this.table.configuration.cssClass = this.CSS_CLASS_CONFIG;
    this.table.configuration.noDataRowMsg =
      this.translate.instant("error.no_result");
  }

  initHeader(): void {
    const thDate = new TableTh();
    thDate.columnId = this.COLUMN_ID_DATE;
    const thDateCell = new ValueCell();
    thDateCell.displayedValue = this.translate.instant(
      "acquisition-log.table.date"
    );
    thDate.valueCells.push(thDateCell);

    const thType = new TableTh();
    thType.columnId = this.COLUMN_ID_TYPE;
    const thTypeCell = new ValueCell();
    thTypeCell.displayedValue = this.translate.instant(
      "acquisition-log.table.type"
    );
    thType.valueCells.push(thTypeCell);

    const thSubtype = new TableTh();
    thSubtype.columnId = this.COLUMN_ID_SUBTYPE;
    const thSubtypeCell = new ValueCell();
    thSubtypeCell.displayedValue = this.translate.instant(
      "acquisition-log.table.subType"
    );
    thSubtype.valueCells.push(thSubtypeCell);

    const thStation = new TableTh();
    thStation.columnId = this.COLUMN_ID_STATION;
    const thStationCell = new ValueCell();
    thStationCell.displayedValue = this.translate.instant(
      "stations.alarmStation.station"
    );
    thStation.valueCells.push(thStationCell);

    const thConnectionType = new TableTh();
    thConnectionType.columnId = this.COLUMN_ID_CONNECTION_TYPE;
    const thConnectionTypeCell = new ValueCell();
    thConnectionTypeCell.displayedValue = this.translate.instant(
      "acquisition-log.table.connectionType"
    );
    thConnectionType.valueCells.push(thConnectionTypeCell);

    const thConnectionName = new TableTh();
    thConnectionName.columnId = this.COLUMN_ID_CONNECTION_NAME;
    const thConnectionNameCell = new ValueCell();
    thConnectionNameCell.displayedValue = this.translate.instant(
      "acquisition-log.table.connectionName"
    );
    thConnectionName.valueCells.push(thConnectionNameCell);

    const thConnectionId = new TableTh();
    thConnectionId.columnId = this.COLUMN_ID_CONNECTION_ID;
    const thConnectionIdCell = new ValueCell();
    thConnectionIdCell.displayedValue = this.translate.instant(
      "acquisition-log.table.connectionId"
    );
    thConnectionId.valueCells.push(thConnectionIdCell);

    const thTagName = new TableTh();
    thTagName.columnId = this.COLUMN_ID_TAG_NAME;
    const thTagNameCell = new ValueCell();
    thTagNameCell.displayedValue = this.translate.instant(
      "acquisition-log.table.tagName"
    );
    thTagName.valueCells.push(thTagNameCell);

    const thData = new TableTh();
    thData.columnId = this.COLUMN_ID_DATA;
    const thDataCell = new ValueCell();
    thDataCell.displayedValue = this.translate.instant(
      "acquisition-log.table.data"
    );
    thData.valueCells.push(thDataCell);

    const thList = [
      thDate,
      thStation,
      thType,
      thSubtype,
      thConnectionType,
      thConnectionId,
      thTagName,
      thData,
    ];
    this.setHeaderCells(thList);
  }

  initDataRow(acquisitionLogs: AgentsEventsPaginatedList): void {
    acquisitionLogs.list.forEach((acquisitionLog) => {
      const tableRows = new TableRows(acquisitionLog);

      // td - date
      const date = new TableTd();
      const dateCell = new ValueCell();
      dateCell.displayedValue = acquisitionLog.datetime;
      dateCell.dataType = "DATE";
      date.valueCells.push(dateCell);

      // td - type
      const type = new TableTd();
      const typeCell = new ValueCell();
      typeCell.displayedValue = acquisitionLog.type
        ? this.translate.instant(
            "acquisition-log.type." + acquisitionLog.type.toLowerCase()
          )
        : this.DISPLAY_NO_DATA;
      if (acquisitionLog.type) {
        typeCell.addCss(acquisitionLog.type.toLowerCase());
      }

      type.valueCells.push(typeCell);

      // td - subtype
      const subType = new TableTd();
      const subTypeCell = new ValueCell();
      subTypeCell.displayedValue = acquisitionLog.subType
        ? acquisitionLog.subType
        : this.DISPLAY_NO_DATA;
      subType.valueCells.push(subTypeCell);

      // td - station
      const station = new TableTd();
      const stationCell = new ValueCell();
      stationCell.displayedValue = acquisitionLog.station
        ? acquisitionLog.station.name +
          " (" +
          acquisitionLog.station.code +
          ")"
        : this.DISPLAY_NO_DATA;
      station.valueCells.push(stationCell);

      // td - connectionType
      const connectionType = new TableTd();
      const connectionTypeCell = new ValueCell();
      connectionTypeCell.displayedValue = acquisitionLog.connectionType
        ? acquisitionLog.connectionType
        : this.DISPLAY_NO_DATA;
      connectionType.valueCells.push(connectionTypeCell);

      // td - connectionName
      const connectionName = new TableTd();
      const connectionNameCell = new ValueCell();
      connectionNameCell.displayedValue = acquisitionLog.connectionName
        ? acquisitionLog.connectionName
        : this.DISPLAY_NO_DATA;
      connectionName.valueCells.push(connectionNameCell);

      // td - connectionId
      const connectionId = new TableTd();
      const connectionIdCell = new ValueCell();
      if (acquisitionLog.connectionId) {
        connectionIdCell.displayedValue = acquisitionLog.connectionId;
      } else {
        connectionIdCell.displayedValue = this.DISPLAY_NO_DATA;
      }


      connectionId.valueCells.push(connectionNameCell);

      // td - TagName
      const tagName = new TableTd();
      const tagNameCell = new ValueCell();
      tagNameCell.displayedValue = acquisitionLog.tagName
        ? acquisitionLog.tagName
        : this.DISPLAY_NO_DATA;
      tagName.valueCells.push(tagNameCell);

      // td - data
      const data = new TableTd();
      const dataCell = new ValueCell();
      dataCell.displayedValue = acquisitionLog.data
        ? acquisitionLog.data
        : this.DISPLAY_NO_DATA;
      data.valueCells.push(dataCell);

      // add cells
      // ================>
      tableRows.cells = [
        date,
        station,
        type,
        subType,
        connectionType,
        connectionId,
        tagName,
        data,
      ];

      // push row
      // ================>
      this.table.dataRow.push(tableRows);
    });
  }

  setHeaderCells(thList: TableTh[]): void {
    this.table.header.cells = thList;
  }
}
